/**
 * Theme Service
 * 
 * Manages themes that correspond to backgrounds.
 * Each background type has an associated theme that affects:
 * - Text colors (e.g., pivot text in top right)
 * - View/module opacity
 * - Other rendering properties
 */

import type { BackgroundType } from '../../rendering/background.js';
import { LoadingScreenType } from '../../types/enums.js';

/**
 * Theme definition
 */
export interface Theme {
  id: string;
  name: string;
  // Text colors
  textColor: string; // Color for pivot text (top right)
  // View/module opacity
  viewOpacity: number; // Base opacity for views/modules (0-1)
  // Logo image
  logoImage: string; // Path to logo image file (e.g., 'logo-smoother.png', 'logo_liquid.png')
  // Loading screen type
  loadingType: LoadingScreenType; // Type of loading screen to show
  // Additional theme properties can be added here
}

/**
 * Available themes
 */
export const themes: Record<string, Theme> = {
  'light': {
    id: 'light',
    name: 'Light',
    textColor: 'rgba(15, 17, 21, 0.85)', // Dark text for light backgrounds
    viewOpacity: 0.20, // Higher opacity for light backgrounds
    logoImage: 'logo-smoother.png', // White logo for light backgrounds
    loadingType: LoadingScreenType.FluidLogo, // Fluid logo loading screen
  },
  'dark': {
    id: 'dark',
    name: 'Dark',
    textColor: 'rgba(255, 255, 255, 0.95)', // White text for dark backgrounds
    viewOpacity: 0.12, // Lower opacity for dark backgrounds
    logoImage: 'logo-smoother.png', // White logo for dark backgrounds
    loadingType: LoadingScreenType.FluidLogo, // Fluid logo loading screen
  },
  'gradient-light': {
    id: 'gradient-light',
    name: 'Gradient Light',
    textColor: 'rgba(15, 17, 21, 0.85)', // Dark text for light gradients
    viewOpacity: 0.20,
    logoImage: 'logo_liquid.png', // Liquid logo for red gradient background
    loadingType: LoadingScreenType.FluidLogo, // Fluid logo loading screen
  },
  'gradient-sky': {
    id: 'gradient-sky',
    name: 'Gradient Sky',
    textColor: 'rgba(15, 17, 21, 0.85)', // Dark text for light sky gradients
    viewOpacity: 0.20,
    logoImage: 'logo_liquid.png', // Liquid logo for sky gradient theme
    loadingType: LoadingScreenType.FluidLogo, // Fluid logo loading screen
  },
  'checkerboard': {
    id: 'checkerboard',
    name: 'Checkerboard',
    textColor: 'rgba(15, 17, 21, 0.85)', // Dark text for checkerboard
    viewOpacity: 0.20,
    logoImage: 'logo_liquid.png', // Liquid logo for checkerboard
    loadingType: LoadingScreenType.FluidLogo, // Fluid logo loading screen
  },
  'video': {
    id: 'video',
    name: 'Video',
    textColor: 'rgba(15, 17, 21, 0.85)', // Dark text for video backgrounds (usually light)
    viewOpacity: 0.20,
    logoImage: 'logo-smoother.png', // White logo for video backgrounds
    loadingType: LoadingScreenType.FluidLogo, // Fluid logo loading screen
  },
  'majaku-privacy': {
    id: 'majaku-privacy',
    name: 'Majaku Privacy',
    textColor: 'rgba(255, 255, 255, 0.95)', // White text for dark privacy screen
    viewOpacity: 0.12, // Lower opacity for dark theme
    logoImage: 'logo-smoother.png', // White logo for dark privacy screen
    loadingType: LoadingScreenType.MajakuPrivacy, // Majaku privacy loading screen
  },
  'drawing': {
    id: 'drawing',
    name: 'Drawing',
    textColor: 'rgba(15, 17, 21, 0.85)', // Dark text for light marble canvas
    viewOpacity: 0.20, // Higher opacity for light backgrounds
    logoImage: 'logo-smoother.png', // Logo for light backgrounds
    loadingType: LoadingScreenType.FluidLogo, // Fluid logo loading screen
  },
  'paper': {
    id: 'paper',
    name: 'Paper',
    textColor: 'rgba(15, 17, 21, 0.90)', // Dark text for paper background
    viewOpacity: 0.25, // Higher opacity for paper backgrounds
    logoImage: 'logo-smoother.png', // Logo for paper mode
    loadingType: LoadingScreenType.FluidLogo, // Fluid logo loading screen
  },
};

/**
 * Map background type to theme ID
 */
export function getThemeIdForBackground(
  backgroundType: BackgroundType,
  backgroundData?: { gradient?: { topColor: string; bottomColor: string } }
): string {
  switch (backgroundType) {
    case 'image':
      return 'light'; // Default to light for image backgrounds
    case 'gradient':
      // Check gradient colors to determine theme
      if (backgroundData?.gradient) {
        const { topColor, bottomColor } = backgroundData.gradient;
        // Sky gradient: #F5FAFF to #DCE9F6 (light blue/white)
        if (topColor === '#F5FAFF' && bottomColor === '#DCE9F6') {
          return 'gradient-sky';
        }
      }
      // Default to gradient-light for other gradients
      return 'gradient-light';
    case 'checkerboard':
      return 'checkerboard';
    case 'video':
      return 'video';
    case 'majaku-privacy':
      return 'majaku-privacy';
    case 'drawing':
      return 'drawing';
    case 'paper':
      return 'paper';
    default:
      return 'light';
  }
}

/**
 * Get theme for a background type
 */
export function getThemeForBackground(
  backgroundType: BackgroundType,
  backgroundData?: { gradient?: { topColor: string; bottomColor: string } }
): Theme {
  const themeId = getThemeIdForBackground(backgroundType, backgroundData);
  return themes[themeId] || themes['light'];
}

/**
 * Get current theme based on background index
 */
export function getCurrentTheme(
  backgroundIndex: number,
  backgrounds: Array<{
    type: BackgroundType;
    gradient?: { type: 'gradient'; topColor: string; bottomColor: string };
  }>
): Theme {
  if (backgrounds.length === 0) {
    return themes['light'];
  }

  const currentBackground = backgrounds[backgroundIndex % backgrounds.length];
  return getThemeForBackground(currentBackground.type, currentBackground);
}

/**
 * Get theme by ID
 */
export function getTheme(themeId: string): Theme {
  return themes[themeId] || themes['light'];
}

